/**
 * Discount Normalizer Utility
 * 
 * Este utilitário detecta e normaliza dados legados de desconto.
 * 
 * PROBLEMA: O código antigo salvava `discount_value` como desconto TOTAL do item.
 * O código novo (orderCalculator) espera que seja desconto POR UNIDADE.
 * 
 * Esta função detecta automaticamente qual semântica foi usada e converte
 * para o padrão atual (por unidade).
 */

export interface ItemWithDiscount {
  discount_value?: number | null;
  discount_percentage?: number | null;
  unit_price: number;
  quantity: number;
}

/**
 * Normaliza o discount_value de um item para garantir que seja POR UNIDADE.
 * 
 * Lógica de detecção:
 * 1. Se discount_value > unit_price E quantity > 1, é impossível ser por unidade
 *    → Converter de total para por unidade (dividir por quantity)
 * 
 * 2. Se discount_percentage existe e o cálculo bate com discount_value sendo total
 *    → Converter de total para por unidade
 * 
 * 3. Caso contrário, assumir que já é por unidade
 * 
 * @param item Item com informações de desconto
 * @returns discount_value normalizado (por unidade)
 */
export function normalizeDiscountValueToPerUnit(item: ItemWithDiscount): number {
  const discountValue = Number(item.discount_value || 0);
  const quantity = Number(item.quantity) || 1;
  const unitPrice = Number(item.unit_price) || 0;
  const discountPercentage = Number(item.discount_percentage || 0);
  
  // Sem desconto, retornar 0
  if (discountValue <= 0) return 0;
  
  // Se quantidade é 1, o valor total e por unidade são iguais
  if (quantity <= 1) return discountValue;
  
  // DETECÇÃO 1: Se discount_value > unit_price, é impossível ser por unidade
  // (desconto não pode ser maior que o preço de uma unidade)
  if (discountValue > unitPrice) {
    const perUnit = discountValue / quantity;
    console.warn(
      `[DiscountNormalizer] Convertendo discount_value de total para por unidade: ` +
      `${discountValue.toFixed(2)} → ${perUnit.toFixed(2)} (qty: ${quantity})`
    );
    return Math.round(perUnit * 100) / 100; // Arredondar para 2 casas decimais
  }
  
  // DETECÇÃO 2: Se temos discount_percentage, verificar se discount_value bate
  // como desconto TOTAL (não por unidade)
  if (discountPercentage > 0) {
    const subtotal = unitPrice * quantity;
    const expectedTotalDiscount = (subtotal * discountPercentage) / 100;
    
    // Se discount_value está próximo do desconto total esperado pela %
    // então foi salvo como total (legado)
    if (Math.abs(expectedTotalDiscount - discountValue) < 0.5) {
      const perUnit = discountValue / quantity;
      console.warn(
        `[DiscountNormalizer] Detectado discount_value legado (via %): ` +
        `${discountValue.toFixed(2)} → ${perUnit.toFixed(2)} (qty: ${quantity}, expected total: ${expectedTotalDiscount.toFixed(2)})`
      );
      return Math.round(perUnit * 100) / 100;
    }
  }
  
  // Assume que já é por unidade
  return discountValue;
}

/**
 * Aplica normalização a um array de itens, retornando itens com discount_value corrigido.
 * 
 * @param items Array de itens para normalizar
 * @returns Array de itens com discount_value normalizado
 */
export function normalizeItemsDiscount<T extends ItemWithDiscount>(items: T[]): T[] {
  return items.map(item => ({
    ...item,
    discount_value: normalizeDiscountValueToPerUnit(item),
  }));
}
